"use client"

import { useCallback, useMemo, useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"

import { STAGE_ORDER, stageHex } from "@/lib/stage-colors"
import { usePutJobsJobIdPipelineStage } from "openapi/api/endpoints"

/** Where a stage sits relative to where the candidate is now. */
export type StageRelation = "back" | "current" | "forward" | "target"

export type StageOption = {
  id: string
  hex: string
  relation: StageRelation
}

/**
 * Moving a candidate along a job's pipeline.
 *
 * The move is a job operation — `PUT /jobs/{jobId}/pipeline/stage` — so it
 * needs a job. Opened from a job's pipeline that is the job in the route; from
 * the candidates list it is the job behind the stage the record is showing.
 * With neither, there is nothing to move along and `canMove` is false.
 */
export function useCandidateStage({
  candidateId,
  jobId,
  currentStage,
}: {
  candidateId: string
  jobId?: string | null
  currentStage?: string
}) {
  const [target, setTarget] = useState<string | undefined>()
  /*
    The dialog outlives any one record — the overlay stays mounted while the
    candidate, its job, or its stage changes underneath. A target picked for the
    previous record would otherwise still be armed, and "Move to submitted"
    would send it against whoever is on screen now.
  */
  const identity = `${candidateId}|${jobId ?? ""}|${currentStage ?? ""}`
  const [targetFor, setTargetFor] = useState(identity)
  if (targetFor !== identity) {
    setTargetFor(identity)
    setTarget(undefined)
  }

  const queryClient = useQueryClient()
  const { mutateAsync: moveStage, isPending: isMoving } =
    usePutJobsJobIdPipelineStage()

  const stages = useMemo<StageOption[]>(() => {
    const currentIndex = currentStage ? STAGE_ORDER.indexOf(currentStage as never) : -1

    return STAGE_ORDER.map((id, index) => {
      let relation: StageRelation
      if (id === target) relation = "target"
      else if (id === currentStage) relation = "current"
      else if (currentIndex >= 0 && index < currentIndex) relation = "back"
      else relation = "forward"

      return { id, hex: stageHex(id), relation }
    })
  }, [currentStage, target])

  /**
   * Returns the stage the candidate landed on, so the caller can react to
   * *which* move happened — hiring someone opens a placement — and `undefined`
   * when nothing moved.
   */
  const move = useCallback(async () => {
    if (!jobId || !target) return undefined

    try {
      await moveStage({ jobId, data: { candidate_id: candidateId, stage: target } })

      await queryClient.invalidateQueries({
        queryKey: [`/jobs/${jobId}/pipeline`],
        exact: false,
      })
      await queryClient.invalidateQueries({
        queryKey: [`/jobs/${jobId}/candidate`],
        exact: false,
      })
      // The candidates table reads stage from the job–candidate links.
      await queryClient.invalidateQueries({
        queryKey: ["/candidates/search"],
        exact: false,
      })

      toast.success(`Moved to ${target}`)
      return target
    } catch (error) {
      toast.error("Could not move the candidate", { description: `${error}` })
      return undefined
    }
  }, [candidateId, jobId, moveStage, queryClient, target])

  return {
    stages,
    target,
    setTarget,
    clearTarget: useCallback(() => setTarget(undefined), []),
    move,
    isMoving,
    canMove: Boolean(jobId),
  }
}
